import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, distinctUntilChanged, filter, map } from 'rxjs';
import { GroupService } from '../../../core/services/group.service';
import { ChatService } from '../../../chat/chat.service';
import { ConnectAgingCardComponent } from './connect-aging-card.component';
import { ConnectClaimCardComponent } from './connect-claim-card.component';
import { ConnectDashboardService } from './connect-dashboard.service';
import { ConnectIconComponent } from './connect-icon.component';
import { ConnectLedgerDialogComponent, ConnectLedgerDialogData } from './connect-ledger-dialog.component';
import {
  ConnectAgingItem,
  ConnectBuckets,
  ConnectClaimItem,
  ConnectDashboard,
  ConnectRequest,
  dashboardStats,
  formatCents,
  requestFor,
  requestTitle,
} from './connect-dashboard.util';
import { ConnectRequestDialogComponent, ConnectRequestDialogData } from './connect-request-dialog.component';

type LoadProblem = 'not-linked' | 'off' | 'failed';

/**
 * The Connect page: the practice's MedicAI Connect dashboard (receivables and collections at a glance), styled exactly
 * as Connect's own. Read from Connect each time the page opens. Patient files open in clinical's split view; accounts
 * and claims open a read-only copy of the patient's Connect ledger.
 */
@Component({
  selector: 'app-connect-dashboard',
  standalone: true,
  imports: [NgIf, NgFor, NgTemplateOutlet, ConnectIconComponent, ConnectAgingCardComponent, ConnectClaimCardComponent],
  templateUrl: './connect-dashboard.component.html',
  styleUrls: ['./connect-dashboard.component.scss'],
  // The cards share this page's styles; every rule sits under .cx-dashboard.
  encapsulation: ViewEncapsulation.None,
})
export class ConnectDashboardComponent implements OnInit, OnDestroy {
  dashboard: ConnectDashboard | null = null;
  stats: ReturnType<typeof dashboardStats> | null = null;
  loading = true;
  problem: LoadProblem | null = null;

  readonly buckets: { key: Exclude<keyof ConnectBuckets, 'incoming'>; heading: string; empty: string }[] = [
    { key: 'current', heading: 'Current (0–29 days)', empty: 'No open claims or invoices from the last 30 days.' },
    { key: '30', heading: '30 days', empty: 'No open claims or invoices 30 to 59 days old.' },
    { key: '60', heading: '60 days', empty: 'No open claims or invoices 60 to 89 days old.' },
    { key: '90', heading: '90 days', empty: 'No open claims or invoices 90 to 119 days old.' },
    { key: '120_plus', heading: '120+ days', empty: 'Nothing older than 120 days.' },
  ];

  /** 0 to 1 once when the board first lands, so the tiles count up as Connect's do. */
  reveal = 0;
  private revealStarted = false;
  private groupId: string | null = null;
  /** The practice, once it is known to have a billing team on MedicAI Connect: the cards then offer a chat. */
  chatGroupId: string | null = null;
  private groupSubscription?: Subscription;
  private request?: Subscription;

  constructor(
    private groupService: GroupService,
    private connectDashboard: ConnectDashboardService,
    private router: Router,
    private dialog: MatDialog,
    private zone: NgZone,
    private chat: ChatService,
  ) {}

  ngOnInit(): void {
    this.groupSubscription = this.groupService.selectedGroup$
      .pipe(
        map((group: any) => group?.id as string | undefined),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
      )
      .subscribe((id) => {
        this.groupId = id;
        this.load();
        this.chatGroupId = null;
        void this.chat.billingTeams(id).then((teams) => {
          if (this.groupId === id && teams.length) this.zone.run(() => (this.chatGroupId = id));
        });
      });
  }

  ngOnDestroy(): void {
    this.groupSubscription?.unsubscribe();
    this.request?.unsubscribe();
  }

  load(): void {
    if (!this.groupId) return;
    this.request?.unsubscribe();
    this.loading = true;
    this.problem = null;
    this.dashboard = null;
    this.request = this.connectDashboard.get(this.groupId).subscribe({
      next: (dashboard) => {
        this.loading = false;
        this.dashboard = dashboard;
        this.stats = dashboardStats(dashboard.buckets, dashboard.summary);
        this.startReveal();
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.problem = error.status === 403 ? 'not-linked' : error.status === 404 ? 'off' : 'failed';
      },
    });
  }

  statInt(n: number): number {
    return Math.round(n * this.reveal);
  }

  statRand(cents: number): string {
    return formatCents(Math.round(cents * this.reveal));
  }

  items(key: Exclude<keyof ConnectBuckets, 'incoming'>): ConnectAgingItem[] {
    return this.dashboard?.buckets[key] ?? [];
  }

  trackAging(_: number, item: ConnectAgingItem): string {
    return item.documentId ?? item.patientId;
  }

  /** "1 claim", "3 claims". */
  countText(n: number, noun: string): string {
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
  }

  rand(cents: number): string {
    return formatCents(cents);
  }

  trackClaim(_: number, item: ConnectClaimItem): string {
    return `${item.batchId}:${item.patientId}`;
  }

  /** The open request from the billing office about a card's patient, if any. */
  requestOf(item: ConnectAgingItem): ConnectRequest | null {
    return requestFor(this.dashboard?.requests, item.patientId);
  }

  requestTitle(request: ConnectRequest): string {
    return requestTitle(request);
  }

  /** A request from the billing office: decline it, or have CURA draft the letter. The board reloads once it is answered. */
  openRequest(request: ConnectRequest): void {
    if (!this.groupId) return;
    const data: ConnectRequestDialogData = { groupId: this.groupId, request };
    this.dialog
      .open(ConnectRequestDialogComponent, { data, panelClass: 'cx-request-dialog', width: '760px', maxWidth: '96vw', autoFocus: false })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'answered') this.load();
      });
  }

  /** The patient's account as Connect shows it, read-only, on the card's document when there is one. */
  openAccount(item: ConnectAgingItem): void {
    this.openLedger(item, item.documentId);
  }

  /** The claim inside the patient's account, opened and in view. */
  openClaim(claim: ConnectClaimItem): void {
    this.openLedger(claim, claim.batchId);
  }

  /**
   * The patient's clinical file, in the split view beside this page. Cards only offer it for this practice's patients.
   * With `linkDetails` the file opens its dialog for linking the medical aid.
   */
  openPatientFile(item: { patient: { clinicalPatientId: string | null } }, linkDetails = false): void {
    const own = item.patient.clinicalPatientId;
    if (own) void this.router.navigate(['/patients', { outlets: { patient: ['file', own] } }],
      linkDetails ? { queryParams: { linkDetails: '1' } } : undefined);
  }

  private openLedger(item: { patientId: string; patient: { clinicalPatientId: string | null } }, focusDocumentId?: string): void {
    if (!this.groupId) return;
    const data: ConnectLedgerDialogData = {
      groupId: this.groupId,
      patientId: item.patientId,
      clinicalPatientId: item.patient.clinicalPatientId,
      focusDocumentId,
    };
    this.dialog
      .open(ConnectLedgerDialogComponent, { data, panelClass: 'cx-ledger-dialog', width: '1100px', maxWidth: '96vw', height: '90vh', autoFocus: false })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'patient-file') this.openPatientFile(item);
      });
  }

  private startReveal(): void {
    if (this.revealStarted) return;
    this.revealStarted = true;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.reveal = 1;
      return;
    }
    const t0 = performance.now();
    this.zone.runOutsideAngular(() => {
      const step = (t: number): void => {
        const p = Math.min(1, (t - t0) / 900);
        this.zone.run(() => (this.reveal = 1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }
}
